import { useTranslation } from 'react-i18next';

/**
 * 权限同步确认弹窗组件
 * 基于 Popconfirm 实现，根据当前用户角色（admin / 非 admin）展示不同内容
 */

import { useGlobalStore } from '@/store';
import { Popconfirm } from '@arco-design/web-react';
import { IconExclamationCircleFill } from '@arco-design/web-react/icon';
import React from 'react';

export interface PermissionSyncPopconfirmProps {
  /** 是否可见 */
  visible?: boolean;
  /** 点击"去同步"时调用的同步函数 */
  onSync: () => Promise<void>;
  /** 关闭弹窗 */
  onClose?: () => void;
  /** 子元素（触发按钮） */
  children: React.ReactNode;
}

/**
 * 权限同步确认弹窗（高阶组件形式）
 * - admin 用户：展示"是否一键同步"确认，并提供"去同步"操作按钮
 * - 非 admin 用户：仅展示提示信息，无操作按钮
 */
export const PermissionSyncPopconfirm: React.FC<PermissionSyncPopconfirmProps> = ({
  visible = true,
  onSync,
  onClose,
  children,
}) => {
  const { t } = useTranslation();
  const isAdmin = useGlobalStore((state) => state.isAdmin);

  const handleSync = async () => {
    await onSync();
    onClose?.();
  };

  // 非 admin 用户：仅展示提示，无确认按钮
  if (!isAdmin) {
    return (
      <Popconfirm
        defaultPopupVisible={visible}
        title={t('components:current.permissions.not.synchronized.please.contact.administrator')}
        icon={<IconExclamationCircleFill style={{ color: 'var(--color-warning-light-4)' }} />}
        cancelText={null}
        okText={t('components:ok')}
        onOk={() => {
          onClose?.();
        }}
      >
        {children}
      </Popconfirm>
    );
  }

  // admin 用户：展示确认对话框，提供"去同步"按钮
  return (
    <Popconfirm
      defaultPopupVisible={visible}
      title={t('components:current.permissions.not.synchronized.sync.with.one.click')}
      icon={<IconExclamationCircleFill style={{ color: 'var(--color-warning-light-4)' }} />}
      okText={t('components:sync.now')}
      cancelText={t('common:cancel')}
      onOk={handleSync}
      onCancel={() => {
        onClose?.();
      }}
    >
      {children}
    </Popconfirm>
  );
};

export default PermissionSyncPopconfirm;
